import { isEmpty } from 'lodash';
import moment from 'moment';
import i18n from '../i18n';
import { Price } from '../queries/ats/titles/types/shared';
import { Source } from '../queries/ats/titles/types/sources';
import { Title } from '../queries/ats/titles/types/title';
import { invariant } from './commonUtils';
import { isWithinFuture, isWithinPast } from './timeUtils';

/**
 * Calculates the relative time based on the start time of a show.
 * The function will return a string that represents the time until the show airs or the time since the show aired.
 *
 * @param startTime The start time of the show.
 * @param prefix The prefix to use for the localized strings. The string location will be `time:${prefix}-past`, `time:${prefix}-present` and `time:${prefix}-future`.
 */
export function localizedRelativeTime(startTime: string, prefix: 'airtime' | 'recording') {
  const now = moment();
  const start = moment(startTime);

  const past = i18n.t(`time:${prefix}-past`);
  const present = i18n.t(`time:${prefix}-present`);
  const future = i18n.t(`time:${prefix}-future`);

  const aWeekAgo = now.clone().subtract(7, 'days');
  const inAWeek = now.clone().add(7, 'days');

  if (start.isBefore(aWeekAgo)) {
    return i18n.t('time:at', {
      prefix: past,
      date: start.format('MMMM Do'),
      time: start.format('HH:mm'),
    });
  }

  if (isWithinPast('hour', start)) {
    return `${present} ${start.fromNow()}`;
  }

  if (isWithinPast('day', start)) {
    const isYesterday = start.isSame(now.clone().subtract(1, 'day'), 'day');

    return i18n.t('time:at', {
      prefix: past,
      date: i18n.t(`time:${isYesterday ? 'yesterday' : 'today'}`),
      time: start.format('HH:mm'),
    });
  }

  if (isWithinPast('week', start)) {
    return i18n.t('time:at', {
      prefix: past,
      date: start.format('dddd'),
      time: start.format('HH:mm'),
    });
  }

  // Within 24 hours in the future
  if (isWithinFuture('day', start, true)) {
    const isTomorrow = start.isSame(now.clone().add(1, 'day'), 'day');

    return i18n.t('time:at', {
      prefix: future,
      date: i18n.t(`time:${isTomorrow ? 'tomorrow' : 'today'}`),
      time: start.format('HH:mm'),
    });
  }

  // Within 1 week in the future
  if (isWithinFuture('week', start)) {
    return i18n.t('time:at', {
      prefix: future,
      date: start.format('dddd'),
      time: start.format('HH:mm'),
    });
  }

  // More than one week in the future
  if (start.isAfter(inAWeek)) {
    return i18n.t('time:at', {
      prefix: future,
      date: start.format('MMMM Do'),
      time: start.format('HH:mm'),
    });
  }

  throw new Error('Could not calculate relative time');
}

export function getLowestPrice(prices?: Price[]): Price | undefined {
  // TODO: Temp fix for gjermund & co to test cvp.
  if (!prices || isEmpty(prices)) {
    return undefined;
  }

  const lowestPrice = prices?.reduce((prev, curr) => {
    if (prev.amount < curr.amount) {
      return prev;
    }

    return curr;
  });

  if (lowestPrice) {
    return lowestPrice;
  }

  return prices[0];
}

export function findRelatedPlaybackOption(title: Title, source: Source) {
  const option = title.playbackOptions.find((opt) => {
    return opt.providerIdentifier === source.id;
  });

  invariant(option !== undefined, `Playback option for "${source.id}" was not found.`);

  return option;
}

export function getClosest(arr: number[], goal: number) {
  return arr.reduce((prev, curr) => (Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev));
}
